import React from "react";
import { useNavigate } from "react-router-dom";
import HabboImage from "../HabboImage";
import { PlainEmoteInput } from "../../services/emoteService";
import EditableBox from "./EditableBox";
import ColorPicker from "../ColorPicker";
import { getBadgeUrl } from "../../utils/badgeUrl";
import { resolveCosmeticUrl } from '../../services/profileColorService';

const _srgbToLinear = (v) => { const x = v / 255; return x <= 0.04045 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4); };
const _lowerLuminance = (hex) => { const h = String(hex || '').trim(); if (!/^#[0-9A-Fa-f]{6}$/.test(h)) return 1; const r = parseInt(h.slice(1, 3), 16), g = parseInt(h.slice(3, 5), 16), b = parseInt(h.slice(5, 7), 16); return 0.2126 * _srgbToLinear(r) + 0.7152 * _srgbToLinear(g) + 0.0722 * _srgbToLinear(b); };

const MOODS = [
    { key: 'happy',       label: 'Happy',       icon: '•' },
    { key: 'excited',     label: 'Excited',     icon: '¥' },
    { key: 'loved',       label: 'Loved',       icon: 'ƒ' },
    { key: 'proud',       label: 'Proud',       icon: 'º' },
    { key: 'relaxed',    label: 'Relaxed',    icon: 'µ' },
    { key: 'silly',       label: 'Silly',       icon: '¿' },
    { key: 'sleepy',      label: 'Sleepy',      icon: 'µ' },
    { key: 'hungry',      label: 'Hungry',      icon: '¬' },
    { key: 'bored',       label: 'Bored',       icon: '÷' },
    { key: 'confused',    label: 'Confused',    icon: '¡' },
    { key: 'anxious',     label: 'Anxious',     icon: '†' },
    { key: 'sad',         label: 'Sad',         icon: '÷' },
    { key: 'angry',       label: 'Angry',       icon: '‡' },
    { key: 'sick',        label: 'Sick',        icon: 'ª' },
    { key: 'motivated',   label: 'Motivated',   icon: '¶' },
    { key: 'creative',    label: 'Creative',    icon: 'Õ' },
    { key: 'gaming',      label: 'Gaming',      icon: '»' },
    { key: 'working',     label: 'Working',     icon: '±' },
    { key: 'vibing',      label: 'Vibing',      icon: '—' },
    { key: 'nostalgic',  label: 'Nostalgic',  icon: '|' },
];

const FR1K_PARTICLES = [
    { kf: 'fr1k-p0', delay: '0s',    dur: '1.8s', size: 7 },
    { kf: 'fr1k-p1', delay: '0.22s', dur: '2.0s', size: 9 },
    { kf: 'fr1k-p2', delay: '0.44s', dur: '1.7s', size: 6 },
    { kf: 'fr1k-p3', delay: '0.67s', dur: '2.1s', size: 8 },
    { kf: 'fr1k-p4', delay: '0.89s', dur: '1.9s', size: 7 },
    { kf: 'fr1k-p5', delay: '1.11s', dur: '2.0s', size: 9 },
    { kf: 'fr1k-p6', delay: '1.33s', dur: '1.8s', size: 6 },
    { kf: 'fr1k-p7', delay: '1.56s', dur: '2.1s', size: 8 },
];

const FR1K_CSS = `@keyframes fr1k-p0{0%{opacity:0;transform:translate(0,0) scale(.3)}15%{opacity:.95}80%{opacity:.85}100%{opacity:0;transform:translate(0px,-32px) scale(.5)}}@keyframes fr1k-p1{0%{opacity:0;transform:translate(0,0) scale(.3)}15%{opacity:.95}80%{opacity:.85}100%{opacity:0;transform:translate(23px,-23px) scale(.5)}}@keyframes fr1k-p2{0%{opacity:0;transform:translate(0,0) scale(.3)}15%{opacity:.95}80%{opacity:.85}100%{opacity:0;transform:translate(32px,0px) scale(.5)}}@keyframes fr1k-p3{0%{opacity:0;transform:translate(0,0) scale(.3)}15%{opacity:.95}80%{opacity:.85}100%{opacity:0;transform:translate(23px,23px) scale(.5)}}@keyframes fr1k-p4{0%{opacity:0;transform:translate(0,0) scale(.3)}15%{opacity:.95}80%{opacity:.85}100%{opacity:0;transform:translate(0px,32px) scale(.5)}}@keyframes fr1k-p5{0%{opacity:0;transform:translate(0,0) scale(.3)}15%{opacity:.95}80%{opacity:.85}100%{opacity:0;transform:translate(-23px,23px) scale(.5)}}@keyframes fr1k-p6{0%{opacity:0;transform:translate(0,0) scale(.3)}15%{opacity:.95}80%{opacity:.85}100%{opacity:0;transform:translate(-32px,0px) scale(.5)}}@keyframes fr1k-p7{0%{opacity:0;transform:translate(0,0) scale(.3)}15%{opacity:.95}80%{opacity:.85}100%{opacity:0;transform:translate(-23px,-23px) scale(.5)}}@keyframes fr1k-glow{0%,100%{opacity:.22;transform:scale(.85)}50%{opacity:.48;transform:scale(1.1)}}`;

const ProfileMoodBubble = ({ mood, isEditing, isOwnProfile, onSelect, onClear }) => {
    const [open, setOpen] = React.useState(false);
    const ref = React.useRef(null);

    React.useEffect(() => {
        if (!open) return;
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const moodObj = MOODS.find(m => m.key === mood);
    const canInteract = isOwnProfile;

    if (!isOwnProfile && !moodObj) return null;

    return (
        <div ref={ref} className="relative flex-shrink-0">
            <button
                type="button"
                onClick={canInteract ? () => setOpen(o => !o) : undefined}
                style={{ fontSize: '9px' }}
                className={[
                    'relative z-[1] inline-block h-[32px] select-none',
                    canInteract ? 'cursor-pointer' : 'cursor-default',
                ].join(' ')}
                title={canInteract ? 'Set your mood' : undefined}
            >
                <div className="relative h-[32px] pl-[22px] pr-[12px]">

                    <div
                        className="absolute inset-y-0 left-0"
                        style={{
                            right: '12px',
                            opacity: moodObj ? 1 : 0.6,
                            backgroundImage: 'url(/img/moodbub.png)',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'left top',
                            backgroundSize: '102px 32px',
                            imageRendering: 'pixelated'
                        }}
                    />

                    <div
                        className="absolute top-0 right-0 w-[12px] h-[32px]"
                        style={{
                            opacity: moodObj ? 1 : 0.6,
                            backgroundImage: 'url(/img/moodbub.png)',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right top',
                            backgroundSize: '102px 32px',
                            imageRendering: 'pixelated'
                        }}
                    />

                    <span
                        className={[
                            'relative -top-[2px] z-[1] h-full flex items-center gap-1 whitespace-nowrap font-volterBold leading-none pointer-events-none',
                            canInteract
                                ? 'text-coal/60'
                                : moodObj
                                    ? 'text-coal/80'
                                    : 'text-coal/30',
                        ].join(' ')}
                    >
{moodObj ? (
    <>
        <span>{moodObj.label}</span>
        <span>{moodObj.icon}</span>
    </>
) : (
    <span>Feeling...</span>
)}
</span>

                </div>
            </button>

            {/* Dropdown picker */}
            {canInteract && open && (
                <div className="absolute top-[24px] left-[16px] z-[200] bg-offwhite border border-black/60 w-40 max-h-56 overflow-y-auto habbo-scroll shadow-[2px_2px_0_0_rgba(0,0,0,0.3)]">
                    {moodObj && (
                        <button
                            type="button"
                            onClick={() => { onClear(); setOpen(false); }}
                            style={{ fontSize: '9px' }}
                            className="w-full text-left px-3 py-1.5 font-volterBold text-red-500 hover:bg-red-50 border-b border-black/10"
                        >
                            ✕ Remove mood
                        </button>
                    )}
                    {MOODS.map(m => (
                        <button
                            key={m.key}
                            type="button"
                            onClick={() => { onSelect(m.key); setOpen(false); }}
                            style={{ fontSize: '9px' }}
                            className={[
                                'w-full text-left px-3 py-1.5 font-volterBold flex items-center gap-2 hover:bg-gold/30 transition-colors',
                                m.key === mood ? 'bg-gold/20' : '',
                            ].join(' ')}
                        >
                            <span className="text-coal">{m.label}</span>
                            <span>{m.icon}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default function BioBox({
    profileUsername,
    bannerUrl,
    effectiveIsEditing,
    effectiveIsOwnProfile,
    handleOpenBannerModal = null,
    openCircleBgModal = null,
    customisation,
    sanitiseHex,
    canShowActions,
    isBlockingTarget,
    actionsDisabled,
    relationshipLoading,
    friendActionLoading,
    isPendingOutgoing,
    onFriendClick,
    friendButtonLabel,
    onBlockClick,
    blockActionLoading,
    blockButtonLabel,
    editingPronouns = false,
    setEditingPronouns = null,
    pronounPart1 = '', pronounPart2 = '', setPronounPart1 = null, setPronounPart2 = null,
    pronounCustom1 = '', pronounCustom2 = '', setPronounCustom1 = null, setPronounCustom2 = null,
    setCustomisationValue = null,
    showOnlyBio = false,
    bio = '', setBio = null,
    bioHtmlObj,
    emoteCtx,
    openEmotePicker = null,
    profileMood,
    saveMood = null,
    isEditing = false,
    guestMode = false,
    isLoggedIn = false,
    openReportModal = null,
    getHeaderColour, setHeaderColour = null,
    getBoxSkin, setBoxSkin = null,
    editingHeader = null, setEditingHeader = null,
    getHeaderTitle, setHeaderTitle = null,
    hasSTFSkin = false, ownedSkinIds = [],
    pinnedBadgeID = null,
    pinnedBadgeUploaded = false,
    pinnedBadgeAchievement = null,
    timeAgo = null,
    // Layout / slot overrides
    wrapperClassName = '',
    editableBoxClassName = 'mb-0',
    editableBoxContentClassName = '',
    stretchContent = false,
    primaryAction = undefined,
}) {
    const navigate = useNavigate();

    const outerCls  = stretchContent ? 'relative flex flex-col flex-1' : 'relative';
    const borderCls = stretchContent ? 'border border-black/50 overflow-hidden bg-offwhite/60 flex flex-col flex-1' : 'border border-black/50 overflow-hidden bg-offwhite/60';
    const lowerWrapCls = stretchContent ? 'relative flex flex-col flex-1' : 'relative';
    const innerCls  = stretchContent ? 'px-3 pt-3 pb-3 flex flex-col flex-1' : 'px-3 pt-3 pb-3';
    const bioWrapCls = stretchContent ? 'mt-2 flex flex-col flex-1' : 'mt-2';
    const bioTextCls = 'border border-black/30 px-3 py-2 h-[92px] overflow-y-auto habbo-scroll-v whitespace-pre-wrap';

    const lowerBgVal = sanitiseHex(customisation?.lowerBg) || null;

    const [badgeTipVisible, setBadgeTipVisible] = React.useState(false);
    const [badgeTipPos, setBadgeTipPos] = React.useState({ top: 0, left: 0 });
    const badgeSlotRef = React.useRef(null);
    const tooltipRef = React.useRef(null);
    const openTip = () => {
        if (!pinnedBadgeAchievement || !badgeSlotRef.current) return;
        const r = badgeSlotRef.current.getBoundingClientRect();
        setBadgeTipPos({ top: r.bottom - 65, left: r.right - 20 });
        setBadgeTipVisible(true);
    };
    React.useEffect(() => {
        if (!badgeTipVisible) return;
        const handler = (e) => {
            if (!badgeSlotRef.current?.contains(e.target) && !tooltipRef.current?.contains(e.target)) {
                setBadgeTipVisible(false);
            }
        };
        document.addEventListener('mousedown', handler);
        document.addEventListener('touchstart', handler);
        return () => { document.removeEventListener('mousedown', handler); document.removeEventListener('touchstart', handler); };
    }, [badgeTipVisible]);
    const badgeBgVal = sanitiseHex(customisation?.badgeBg) || null;
    const isFr1kPinned = pinnedBadgeID === 'FR1K';
    const lowerTextPrimary = lowerBgVal ? (_lowerLuminance(lowerBgVal) < 0.16 ? '#f5f5f0' : '#222222') : null;
    const lowerTextMuted   = lowerBgVal ? (_lowerLuminance(lowerBgVal) < 0.16 ? 'rgba(245,245,240,0.6)' : 'rgba(34,34,34,0.6)') : null;

    const showPronounsRow = (effectiveIsEditing && effectiveIsOwnProfile) || !!customisation?.pronouns;

    return (
        <EditableBox boxKey="bio" theme="default" header="Bio"
                     wrapperClassName={wrapperClassName}
                     className={editableBoxClassName}
                     contentClassName={editableBoxContentClassName}
                     isEditing={effectiveIsEditing} isOwnProfile={effectiveIsOwnProfile}
                     getHeaderColour={getHeaderColour} setHeaderColour={setHeaderColour} getBoxSkin={getBoxSkin} setBoxSkin={setBoxSkin}
                     editingHeader={editingHeader} setEditingHeader={setEditingHeader}
                     headerTitle={getHeaderTitle("bio")} setHeaderTitle={setHeaderTitle}
                     isStaff={hasSTFSkin} ownedSkinIds={ownedSkinIds}
                     onHeaderProps={{ isEditing: effectiveIsEditing, isOwnProfile: effectiveIsOwnProfile, getHeaderColour, setHeaderColour, getBoxSkin, setBoxSkin, editingHeader, setEditingHeader, getHeaderTitle, setHeaderTitle }}>
            {isFr1kPinned && <style>{FR1K_CSS}</style>}
            <div className={outerCls}>
                <div className={borderCls}>
                    <div className="relative h-[160px] w-full bg-coal_dark overflow-hidden group border-b border-black/50">
                        {bannerUrl && (
                            <img src={bannerUrl} alt="Banner" className="w-full h-full object-cover" style={{ imageRendering: "pixelated" }} />
                        )}
                        {effectiveIsEditing && effectiveIsOwnProfile && (
                            <button
                                type="button"
                                onClick={handleOpenBannerModal}
                                className="absolute bottom-2 right-2 z-10 flex items-center gap-1.5 bg-offwhite/90 border border-black/60 px-2 py-1 text-xs text-coal hover:bg-gold transition-colors cursor-pointer shadow-[2px_2px_0_0_rgba(0,0,0,0.3)]"
                            >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect x="3" y="3" width="18" height="14" rx="1" stroke="currentColor" strokeWidth="2"/>
                                    <path d="M3 14l5-5 4 4 3-3 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <circle cx="8" cy="8" r="1.5" fill="currentColor"/>
                                </svg>
                                Change Banner
                            </button>
                        )}
                    </div>
                    <div className={lowerWrapCls} style={lowerBgVal ? { backgroundColor: lowerBgVal } : undefined}>
                        {effectiveIsEditing && effectiveIsOwnProfile && (
                            <div className="absolute top-1 right-1 z-10 flex items-center gap-1">
                                <ColorPicker
                                    value={lowerBgVal || '#f5f0e8'}
                                    onChange={(e) => setCustomisationValue('lowerBg', e.target.value)}
                                    className="w-6 h-6"
                                    title="Content area background colour"
                                />
                            </div>
                        )}
                    <div className={innerCls}>
                        <div className={stretchContent ? 'flex gap-3 flex-1' : 'flex gap-3 items-start'}>

                            {/* ── Left column: avatar + badge slot ── */}
                            <div className="flex-shrink-0 flex flex-col gap-2">
                                <div className="relative w-[92px] h-[92px]">
                                    {effectiveIsEditing && effectiveIsOwnProfile && (
                                        <button
                                            type="button"
                                            onClick={openCircleBgModal}
                                            className="absolute -top-1 -right-1 z-[100] w-7 h-7 flex items-center justify-center bg-offwhite border border-black/50 rounded cursor-pointer hover:bg-gold transition-colors"
                                            title="Change avatar background"
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                                                <circle cx="12" cy="12" r="3" fill="currentColor"/>
                                                <path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.93 4.93l2.12 2.12M16.95 16.95l2.12 2.12M4.93 19.07l2.12-2.12M16.95 7.05l2.12-2.12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                            </svg>
                                        </button>
                                    )}
                                    <div
                                        className="w-[92px] h-[92px] rounded border border-black/50 flex items-center justify-center overflow-hidden transition-colors duration-200"
                                        style={/^\/img\/|^\/circlebgassets\/|^https?:\/\//.test(String(customisation?.avatarBg ?? ''))
                                            ? { backgroundImage: `url(${resolveCosmeticUrl(customisation.avatarBg)})`, backgroundSize: 'cover', backgroundPosition: 'center' }
                                            : { backgroundColor: sanitiseHex(customisation?.avatarBg) || 'rgba(255, 255, 255, 1)' }
                                        }
                                    >
                                        <HabboImage habbo={profileUsername} hotel="COM" size="b" direction={2} head_direction={2} gesture="sml" className="relative mt-14" style={{ imageRendering: "pixelated", scale: 2 }} alt={`${profileUsername} avatar`} />
                                    </div>

                                    {/* Mood bubble — top-right of avatar box, tail at right edge, body extends right */}
                                    <div className="absolute top-[-8px] left-[70px] z-[50]">
                                        <ProfileMoodBubble
                                            mood={profileMood}
                                            isEditing={isEditing}
                                            isOwnProfile={effectiveIsOwnProfile}
                                            onSelect={(key) => saveMood && saveMood(key)}
                                            onClear={() => saveMood && saveMood('')}
                                        />
                                    </div>
                                </div>

                                {/* Badge slot */}
                                <div className="relative">
                                    <div
                                        ref={badgeSlotRef}
                                        className={["w-[92px] h-[92px] rounded border border-black/30 flex items-center justify-center relative overflow-hidden", pinnedBadgeID && pinnedBadgeAchievement ? 'cursor-pointer' : ''].join(' ')}
                                        style={{ backgroundColor: badgeBgVal || 'rgba(255,255,255,0.4)' }}
                                        onMouseEnter={openTip}
                                        onMouseLeave={() => setBadgeTipVisible(false)}
                                        onClick={() => badgeTipVisible ? setBadgeTipVisible(false) : openTip()}
                                    >
                                        {effectiveIsEditing && effectiveIsOwnProfile && (
                                            <div className="absolute top-0.5 right-0.5 z-10">
                                                <ColorPicker
                                                    value={badgeBgVal || '#f5f0e8'}
                                                    onChange={(e) => setCustomisationValue('badgeBg', e.target.value)}
                                                    className="w-6 h-6"
                                                    title="Badge background colour"
                                                />
                                            </div>
                                        )}
                                        {isFr1kPinned && (
                                            <div aria-hidden="true" style={{ position: 'absolute', width: '70px', height: '70px', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', borderRadius: '50%', background: 'radial-gradient(circle, rgba(218,165,0,0.52) 0%, rgba(180,130,0,0.16) 45%, transparent 70%)', pointerEvents: 'none', animation: 'fr1k-glow 2.2s ease-in-out infinite', willChange: 'transform, opacity', zIndex: 0 }} />
                                        )}
                                        {pinnedBadgeID ? (
                                            <img
                                                src={getBadgeUrl(pinnedBadgeID, pinnedBadgeUploaded)}
                                                alt="Pinned badge"
                                                style={{
                                                    imageRendering: 'pixelated',
                                                    ...(isFr1kPinned ? { position: 'relative', zIndex: 1, filter: 'drop-shadow(0 0 4px rgba(218,165,0,0.95)) drop-shadow(0 0 9px rgba(218,165,0,0.45))' } : {}),
                                                }}
                                            />
                                        ) : (
                                            <span className="text-coal/25 font-['Jersey_10'] text-3xl leading-none">?</span>
                                        )}
                                    </div>
                                    {/* Particle overlay — outside overflow-hidden so particles aren't clipped */}
                                    {isFr1kPinned && (
                                        <div aria-hidden="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 5 }}>
                                            {FR1K_PARTICLES.map(p => (
                                                <span key={p.kf} style={{ position: 'absolute', left: '50%', top: '50%', width: `${p.size}px`, height: `${p.size}px`, marginLeft: `-${p.size / 2}px`, marginTop: `-${p.size / 2}px`, borderRadius: '50%', background: 'radial-gradient(circle at 38% 38%, #fffbb0 0%, #e8b800 55%, #c48000 100%)', pointerEvents: 'none', animation: `${p.kf} ${p.dur} ease-out ${p.delay} infinite backwards`, willChange: 'transform, opacity' }} />
                                            ))}
                                        </div>
                                    )}
                                    {badgeTipVisible && pinnedBadgeAchievement && (
                                        <div ref={tooltipRef} style={{ position: 'fixed', top: badgeTipPos.top, left: badgeTipPos.left, zIndex: 9999 }} className="bg-offwhite border border-black/40 shadow-[2px_2px_0_0_rgba(0,0,0,0.3)] px-3 py-2 text-coal min-w-[200px] max-w-[220px]">
                                            <div className="font-['Jersey_10'] text-lg leading-none mb-1">{pinnedBadgeAchievement.name}</div>
                                            {pinnedBadgeAchievement.description && (
                                                <div className="text-xs text-coal/80 mb-1.5">{pinnedBadgeAchievement.description}</div>
                                            )}
                                            <div className="border-t border-black/15 pt-1">
                                                {timeAgo && pinnedBadgeAchievement.dateAchieved && (
                                                    <div className="text-[10px] text-coal/70">Gained {timeAgo(pinnedBadgeAchievement.dateAchieved)}</div>
                                                )}
                                                {pinnedBadgeAchievement.earnedCount != null && (
                                                    <div className="text-[10px] text-coal/60">
                                                        Claimed by <span className="font-bold text-coal/80">{pinnedBadgeAchievement.earnedCount.toLocaleString()}</span> user{pinnedBadgeAchievement.earnedCount !== 1 ? 's' : ''}
                                                        {pinnedBadgeAchievement.earnedPct != null && <span> ({pinnedBadgeAchievement.earnedPct}%)</span>}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* ── Right column: username, pronouns, actions, bio ── */}
                            <div className={["flex-1 flex flex-col min-w-0", (customisation?.pronouns || (effectiveIsEditing && effectiveIsOwnProfile)) ? "pt-[18px]" : "pt-[38px]"].join(" ")}>
                                <span className="font-['Jersey_10'] text-3xl leading-none" style={{ color: lowerTextPrimary || '#222222' }}>{profileUsername}</span>

                                {/* Pronouns row */}
                                {(customisation?.pronouns || (effectiveIsEditing && effectiveIsOwnProfile)) && (
                                    <div className="min-h-[20px] flex items-center -mt-0.5 " style={{ color: lowerTextPrimary || '#222222' }}>
                                        {effectiveIsEditing && effectiveIsOwnProfile ? (
                                            editingPronouns ? (
                                                <div className="flex items-center gap-1 flex-wrap" onPointerDown={(e) => e.stopPropagation()}>
                                                    {(pronounPart1 === "custom" || pronounPart2 === "custom") ? (
                                                        <>
                                                            <button
                                                                type="button"
                                                                onClick={() => { setPronounPart1(""); setPronounPart2(""); setPronounCustom1(""); setPronounCustom2(""); }}
                                                                className="h-6 w-6 flex items-center justify-center hover:bg-black/10 rounded transition-colors "
                                                                title="Back to list"
                                                            >
                                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                                                            </button>
                                                            <input
                                                                type="text"
                                                                value={pronounCustom1}
                                                                onChange={(e) => setPronounCustom1(e.target.value.replace(/[^a-zA-Z]/g, "").slice(0, 7))}
                                                                placeholder="e.g. they"
                                                                className="text-sm bg-white border border-black/40 rounded px-2 py-0.5 text-coal placeholder:text-coal/40 focus:outline-none focus:border-black/60 w-20"
                                                                autoFocus
                                                            />
                                                            <span className="text-coal/50 text-sm">/</span>
                                                            <input
                                                                type="text"
                                                                value={pronounCustom2}
                                                                onChange={(e) => setPronounCustom2(e.target.value.replace(/[^a-zA-Z]/g, "").slice(0, 7))}
                                                                placeholder="e.g. them"
                                                                className="text-sm bg-white border border-black/40 rounded px-2 py-0.5 text-coal placeholder:text-coal/40 focus:outline-none focus:border-black/60 w-20"
                                                            />
                                                        </>
                                                    ) : (
                                                        <>
                                                            <select
                                                                value={pronounPart1}
                                                                onChange={(e) => { if (e.target.value === "custom") { setPronounPart1("custom"); setPronounPart2("custom"); setPronounCustom1(""); setPronounCustom2(""); } else { setPronounPart1(e.target.value); } }}
                                                                className="text-sm bg-white border border-black/40 rounded px-1.5 py-0.5 text-coal focus:outline-none focus:border-black/60"
                                                            >
                                                                <option value="">—</option>
                                                                <option value="he">he</option>
                                                                <option value="she">she</option>
                                                                <option value="they">they</option>
                                                                <option value="xe">xe</option>
                                                                <option value="ze">ze</option>
                                                                <option value="fae">fae</option>
                                                                <option value="it">it</option>
                                                                <option value="any">any</option>
                                                                <option value="custom">custom…</option>
                                                            </select>
                                                            <span className="text-coal/50 text-sm">/</span>
                                                            <select
                                                                value={pronounPart2}
                                                                onChange={(e) => { if (e.target.value === "custom") { setPronounPart1("custom"); setPronounPart2("custom"); setPronounCustom1(""); setPronounCustom2(""); } else { setPronounPart2(e.target.value); } }}
                                                                className="text-sm bg-white border border-black/40 rounded px-1.5 py-0.5 text-coal focus:outline-none focus:border-black/60"
                                                            >
                                                                <option value="">—</option>
                                                                <option value="him">him</option>
                                                                <option value="her">her</option>
                                                                <option value="them">them</option>
                                                                <option value="xem">xem</option>
                                                                <option value="hir">hir</option>
                                                                <option value="faer">faer</option>
                                                                <option value="its">its</option>
                                                                <option value="any">any</option>
                                                                <option value="custom">custom…</option>
                                                            </select>
                                                        </>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const p1 = (pronounPart1 === "custom" || pronounPart2 === "custom") ? pronounCustom1.trim() : pronounPart1;
                                                            const p2 = (pronounPart1 === "custom" || pronounPart2 === "custom") ? pronounCustom2.trim() : pronounPart2;
                                                            const combined = (p1 && p2) ? `${p1}/${p2}` : (p1 || p2 || "");
                                                            setCustomisationValue("pronouns", combined);
                                                            setEditingPronouns(false);
                                                        }}
                                                        className="h-6 w-6 flex items-center justify-center hover:bg-black/10 rounded transition-colors"
                                                        title="Save pronouns"
                                                    >
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setEditingPronouns(false)}
                                                        className="h-6 w-6 flex items-center justify-center hover:bg-black/10 rounded transition-colors text-coal/50"
                                                        title="Cancel"
                                                    >
                                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const existing = customisation?.pronouns || "";
                                                        const parts = existing.split("/");
                                                        const knownPart1 = ["he","she","they","xe","ze","fae","it","any"];
                                                        const knownPart2 = ["him","her","them","xem","hir","faer","its","any"];
                                                        const p1 = parts[0] || "";
                                                        const p2 = parts[1] || "";
                                                        if (knownPart1.includes(p1)) { setPronounPart1(p1); setPronounCustom1(""); } else { setPronounPart1(p1 ? "custom" : ""); setPronounCustom1(p1); }
                                                        if (knownPart2.includes(p2)) { setPronounPart2(p2); setPronounCustom2(""); } else { setPronounPart2(p2 ? "custom" : ""); setPronounCustom2(p2); }
                                                        setEditingPronouns(true);
                                                    }}
                                                    className="flex items-center gap-1 px-1.5 py-0.5 border border-black/30 rounded  hover:text-coal hover:border-black/50 hover:bg-black/5 transition-colors"
                                                    title="Set pronouns"
                                                >
                                                    {customisation?.pronouns ? (
                                                        <span className="text-sm font-sans ">{customisation.pronouns}</span>
                                                    ) : null}
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/>
                                                    </svg>
                                                </button>
                                            )
                                        ) : (
                                            customisation?.pronouns ? (
                                                <span className="text-sm font-sans ">[{customisation.pronouns}]</span>
                                            ) : null
                                        )}
                                    </div>
                                )}

                                {canShowActions && (
                                    <div className="flex items-center gap-1.5 mt-1">
                                        {!isBlockingTarget && (
                                            <button type="button" onClick={onFriendClick} disabled={actionsDisabled || relationshipLoading || friendActionLoading || isPendingOutgoing} className="px-2 py-0.5 border border-black/50 bg-offwhite text-coal text-xs rounded flex items-center gap-1 whitespace-nowrap transition-colors duration-150 hover:bg-gold hover:text-coal active:bg-gold/90 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-offwhite">
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" /><circle cx="8.5" cy="7" r="4" stroke="currentColor" strokeWidth="2" /><path d="M20 8v6" stroke="currentColor" strokeWidth="2" /><path d="M23 11h-6" stroke="currentColor" strokeWidth="2" /></svg>
                                                {friendButtonLabel}
                                            </button>
                                        )}
                                        <button type="button" onClick={onBlockClick} disabled={actionsDisabled || relationshipLoading || blockActionLoading} className="px-2 py-0.5 border border-black/50 bg-offwhite text-coal text-xs rounded flex items-center gap-1 whitespace-nowrap transition-colors duration-150 hover:bg-red-600 hover:text-white active:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-offwhite disabled:hover:text-coal">
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" /><path d="M7.5 7.5l9 9" stroke="currentColor" strokeWidth="2" /></svg>
                                            {blockButtonLabel}
                                        </button>
                                    </div>
                                )}

                                <div className={bioWrapCls} style={{ color: lowerTextPrimary || '#222222' }}>
                                    {showOnlyBio ? (
                                        <div className="border border-black/30 px-3 py-2 min-h-[92px] flex items-center justify-center">You have blocked this user.</div>
                                    ) : effectiveIsEditing ? (
                                        <PlainEmoteInput
                                            value={bio}
                                            onChange={setBio}
                                            placeholder="Write a short bio..."
                                            emoteCtx={emoteCtx}
                                            maxLength={200}
                                            multiLine
                                            className="w-full h-[92px] overflow-y-auto habbo-scroll-v border border-black/50  px-3 py-2 "
                                            onOpenEmotePicker={openEmotePicker}
                                            style={{ color: lowerTextPrimary || '#222222' }}
                                        />
                                    ) : (
                                        <div
                                            className={bioTextCls}
                                            dangerouslySetInnerHTML={bioHtmlObj}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    </div>
                </div>

            </div>
            <div className="mt-2 px-2 flex flex-wrap items-center justify-center gap-2 shrink-0">
                {primaryAction !== undefined ? primaryAction : (
                    <button type="button" onClick={() => navigate(`/player/COM/${encodeURIComponent(profileUsername)}`)} className="px-3 py-1 border border-black/50 bg-offwhite text-coal text-sm rounded transition-colors duration-150 hover:bg-gold hover:text-coal active:bg-gold/90">Player Lookup</button>
                )}
                {!guestMode && <button type="button" onClick={openReportModal} disabled={!isLoggedIn || effectiveIsOwnProfile} className="px-3 py-1 border border-black/50 bg-offwhite text-coal text-sm rounded transition-colors duration-150 hover:bg-gold hover:text-coal active:bg-gold/90 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-offwhite disabled:hover:text-coal">Report Profile</button>}
            </div>
        </EditableBox>
    );
}
